import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { motion } from 'motion/react'
import { memo, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Jobibot, LiveField } from '../../rapid-ui'
import { logger } from '../../utilities'
import { ColorProvider } from '../../views'
import { AiGeneratedWatermark } from '../ai-generated-label'
import { CanvasContainer, type ExternalCanvasData, initialCanvasData } from '../canvas'
import { CanvasData } from '../canvas/data'
import { CampaignImage, EmptyImageHint, useImageCache, useIsImageSlotEmpty } from '../images'
import { ImageOverlay } from '../images/image-overlay'
import { Transformer } from '../simulator-tools'
import {
	activeFeedImageToolAtom,
	campaignDetailsBrandingAtom,
	campaignDetailsLogoAtom,
	customFontsAtom,
	customFontsLoadingAtom,
	editorFocusTargetAtom,
	feedAdAiImageryDetectedAtom,
	feedAdAtom,
	feedAdCanvasLoadCounterAtom,
	feedAdCtaTextAtom,
	feedAdDescriptionTextAtom,
	feedAdHasGeneratedContentAtom,
	feedAdHeadlineTextAtom,
	feedAdImageIdAtom,
	feedAdLocationAtom,
	feedAdMetaBackdropTransformAtom,
	feedAdMetaCanvasDocumentAtom,
	feedAdMetaCanvasEnabledAtom,
	feedAdMetaCardsAtom,
	feedAdMetaCtaTextAtom,
	feedAdMetaForceAiGeneratedLabelAtom,
	feedAdMetaGeneralContentHideAtom,
	feedAdMetaLogoBackgroundFillColorAtom,
	feedAdMetaOverlayAtom,
	feedAdMetaStackOrientationAtom,
	feedAdPrimaryTextAtom,
	feedAdStatusAtom,
	feedAdSubtitleAtom,
	feedAdTitleAtom,
	orgAssetsAtom,
	showCanvasToolbarAtom,
} from '../state'
import { FeedAdContentLayer } from './feed-ad-content-layer'

// The image is sized against these too, so they must stay the card's exact size or the
// backdrop shows around it.
const FRAME_WIDTH = 480
const FRAME_HEIGHT = 600

const fallbackPalette = {
	foreground: 'rgba(255,255,255,1)',
	background: 'rgba(0,0,0,1)',
	accentForeground: 'rgba(255,255,255,1)',
	accentBackground: 'rgba(0,0,0,1)',
}

export const FeedAdSimulator = memo(function FeedAdSimulator() {
	const { t } = useTranslation('editor')
	const feedAd = useAtomValue(feedAdAtom)

	const [primaryText, setPrimaryText] = useAtom(feedAdPrimaryTextAtom)
	const [headlineText, setHeadlineText] = useAtom(feedAdHeadlineTextAtom)
	const [descriptionText, setDescriptionText] = useAtom(feedAdDescriptionTextAtom)
	const [metaCtaText, setMetaCtaText] = useAtom(feedAdMetaCtaTextAtom)
	const imageId = useAtomValue(feedAdImageIdAtom)

	const overlay = useAtomValue(feedAdMetaOverlayAtom)

	const feedAdStatus = useAtomValue(feedAdStatusAtom)
	const hasGeneratedContent = useAtomValue(feedAdHasGeneratedContentAtom)
	const isImageToolExecuting = useAtomValue(activeFeedImageToolAtom)
	const isGeneratingText = feedAdStatus === 'generating' && !hasGeneratedContent
	// Text should flash during generation or text tool execution (not image tools)
	const shouldFlashContent = ((feedAdStatus === 'generating' && hasGeneratedContent)
		|| feedAdStatus === 'tool-executing') && !isImageToolExecuting
	// Image should flash during image tool execution
	const shouldFlashImage = isImageToolExecuting

	const cardConfiguration = useAtomValue(feedAdMetaCardsAtom)

	const [backdropTransform, setBackdropTransform] = useAtom(feedAdMetaBackdropTransformAtom)
	const focusTarget = useAtomValue(editorFocusTargetAtom)

	const stackOrientation = useAtomValue(feedAdMetaStackOrientationAtom)

	const hide = useAtomValue(feedAdMetaGeneralContentHideAtom)
	const branding = useAtomValue(campaignDetailsBrandingAtom)
	const logoBackgroundFillColor = useAtomValue(feedAdMetaLogoBackgroundFillColorAtom)
	const forceAiGeneratedLabel = useAtomValue(feedAdMetaForceAiGeneratedLabelAtom)
	const setAiImageryDetected = useSetAtom(feedAdAiImageryDetectedAtom)

	// Canvas state
	const canvasEnabled = useAtomValue(feedAdMetaCanvasEnabledAtom)
	const canvasDocument = useAtomValue(feedAdMetaCanvasDocumentAtom)
	const setCanvasDocument = useSetAtom(feedAdMetaCanvasDocumentAtom)
	const canvasLoadCounter = useAtomValue(feedAdCanvasLoadCounterAtom)
	const showCanvasToolbar = useAtomValue(showCanvasToolbarAtom)

	// External canvas data atoms
	const [title, setTitle] = useAtom(feedAdTitleAtom)
	const [subtitle, setSubtitle] = useAtom(feedAdSubtitleAtom)
	const [location, setLocation] = useAtom(feedAdLocationAtom)
	const [ctaText, setCtaText] = useAtom(feedAdCtaTextAtom)
	const logo = useAtomValue(campaignDetailsLogoAtom)
	const orgAssets = useAtomValue(orgAssetsAtom)
	const customFonts = useAtomValue(customFontsAtom)
	const customFontsLoading = useAtomValue(customFontsLoadingAtom)

	const { getImage } = useImageCache()
	const backgroundUrl = getImage(imageId ?? '').url
	const hasImage = backgroundUrl !== ''
	const isImageSlotEmpty = useIsImageSlotEmpty(imageId)
	const logoUrl = logo?.url ?? ''

	const externalCanvasData = useMemo<ExternalCanvasData>(() => ({
		title: title ?? '',
		subtitle: subtitle ?? '',
		location: location ?? '',
		cta: ctaText ?? '',
		logoUrl,
		backgroundUrl,
		foregroundColor: branding?.foreground ?? fallbackPalette.foreground,
		backgroundColor: branding?.background ?? fallbackPalette.background,
		accentForegroundColor: branding?.accentForeground ?? fallbackPalette.accentForeground,
		accentBackgroundColor: branding?.accentBackground ?? fallbackPalette.accentBackground,
	}), [title, subtitle, location, ctaText, logoUrl, backgroundUrl, branding])

	const handleExternalCanvasDataChange = useCallback((partial: Partial<ExternalCanvasData>) => {
		if (partial.title !== undefined) { setTitle(partial.title) }
		if (partial.subtitle !== undefined) { setSubtitle(partial.subtitle) }
		if (partial.location !== undefined) { setLocation(partial.location) }
		if (partial.cta !== undefined) { setCtaText(partial.cta) }
	}, [setTitle, setSubtitle, setLocation, setCtaText])

	const parsedCanvasDocument = useMemo(() => {
		if (typeof canvasDocument !== 'string') { return initialCanvasData }
		try {
			const raw = JSON.parse(canvasDocument)
			const result = CanvasData.safeParse(raw)
			if (!result.success) {
				logger.error(
					"Canvas document schema validation failed — falling back to default. User's saved design was discarded.",
					{
						category: 'canvas',
						data: {
							simulator: 'feed',
							zodErrors: result.error.issues,
							rawLayerTypes: raw?.layers ? Object.values(raw.layers).map((l: any) => l?.type) : [],
						},
					},
				)
				return initialCanvasData
			}
			return result.data
		} catch (err) {
			logger.error(
				"Failed to parse canvas document JSON — falling back to default. User's saved design was discarded.",
				{
					category: 'canvas',
					data: { simulator: 'feed', error: String(err) },
				},
			)
			return initialCanvasData
		}
	}, [canvasDocument])

	const handleCanvasDocumentChange = useCallback((doc: CanvasData) => {
		setCanvasDocument(JSON.stringify(doc))
	}, [setCanvasDocument])

	if (!feedAd || !cardConfiguration || !backdropTransform || !overlay) {
		return null
	}

	return (
		<ColorProvider brandingPalette={branding ?? fallbackPalette}>
			<div
				className="relative z-10"
				style={{
					color: branding?.foreground ?? fallbackPalette.foreground,
					'--logo-background-fill-color': logoBackgroundFillColor,
				} as React.CSSProperties}
			>
				<motion.div
					initial={{
						scale: 0.98,
						translateY: 10,
						opacity: 0,
					}}
					animate={{
						scale: 1,
						translateY: 0,
						opacity: 1,
					}}
					transition={{
						duration: 0.3,
						translateX: { 'duration': 0.9, 'bounce': 0.3, type: 'spring' },
					}}
					className="ring perspective-dramatic ring-grey-200 bg-white w-[480px] box-content flex flex-col rounded-sm p-4 shadow-lg"
				>
					<div
						className="flex items-center gap-2"
						style={{
							fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
						}}
					>
						<div className="h-12 w-12 flex items-center justify-center">
							<Jobibot size={40} />
						</div>
						<div className="">
							<div className="font-medium text-grey-900">Jobilla</div>
							<div className="text-grey-600 text-sm">{t('simulator.feedAd.sponsored')}</div>
						</div>
					</div>
					<div
						className={`text-sm mt-4 text-grey-900 ${shouldFlashContent ? 'animate-pulse' : ''}`}
						style={{
							fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
						}}
					>
						<LiveField
							isGenerating={isGeneratingText}
							value={primaryText ?? ''}
							onChange={setPrimaryText}
						/>
					</div>
					<div
						className="mt-4 relative transition-all overflow-hidden"
						style={{
							backgroundColor: branding?.background ?? fallbackPalette.background,
							color: 'white',
							width: FRAME_WIDTH,
							height: FRAME_HEIGHT, // 4:5 aspect ratio
						}}
						data-render-me={true}
						data-screenshot-font-fix
					>
						{canvasEnabled
							? (
								<>
									<CanvasContainer
										key={canvasLoadCounter}
										width={480}
										height={600}
										document={parsedCanvasDocument}
										onDocumentChange={handleCanvasDocumentChange}
										externalCanvasData={externalCanvasData}
										onExternalCanvasDataChange={handleExternalCanvasDataChange}
										orgAssets={orgAssets}
										customFonts={customFonts}
										customFontsLoading={customFontsLoading}
										isGenerating={isGeneratingText}
										simulatorType="feed"
										showToolbar={showCanvasToolbar}
									/>
									{isImageSlotEmpty && <EmptyImageHint />}
								</>
							)
							: (
								<>
									<div
										className={`absolute inset-0 grid overflow-hidden ${
											shouldFlashImage ? 'animate-pulse' : ''
										}`}
									>
										<div
											className="absolute inset-0 flex items-center justify-center"
											style={stackOrientation === 'horizontal'
												? {
													width: '100%',
													marginLeft: cardConfiguration.show ? '20%' : '0%',
												}
												: {
													height: '100%',
													marginTop: cardConfiguration.show ? '-25%' : '0%',
												}}
										>
											<Transformer
												value={backdropTransform}
												onChange={setBackdropTransform}
												passthroughClassName=""
												enableTransform={focusTarget.target === 'feed-ad(backdrop)'}
											>
												<div className="size-full grid relative">
													<CampaignImage
														imageId={imageId ?? ''}
														color={branding?.background ?? fallbackPalette.background}
														containerWidth={FRAME_WIDTH}
														containerHeight={FRAME_HEIGHT}
													/>
												</div>
											</Transformer>
										</div>
										{hasImage && <ImageOverlay overlay={overlay} />}
									</div>
									{!hide && <FeedAdContentLayer />}
								</>
							)}
						<AiGeneratedWatermark
							forceVisible={forceAiGeneratedLabel}
							onDetectedChange={setAiImageryDetected}
						/>
					</div>

					<div
						className={`grid grid-cols-[1fr_auto] gap-4 p-3 items-center bg-grey-100 ${
							shouldFlashContent ? 'animate-pulse' : ''
						}`}
						style={{
							fontFamily: 'Segoe UI Historic, Segoe UI, Helvetica, Arial, sans-serif',
						}}
					>
						<div className="flex flex-col">
							<div className="text-grey-600 text-sm">FORMS.JOBILLA.COM</div>
							<div className={`font-medium text-grey-900 ${shouldFlashContent ? 'animate-pulse' : ''}`}>
								<LiveField
									isGenerating={isGeneratingText}
									value={headlineText ?? ''}
									onChange={setHeadlineText}
								/>
							</div>
							<div className={`text-grey-600 text-sm ${shouldFlashContent ? 'animate-pulse' : ''}`}>
								<LiveField
									isGenerating={isGeneratingText}
									value={descriptionText ?? ''}
									onChange={setDescriptionText}
								/>
							</div>
						</div>
						<div>
							<div
								className={`text-grey-600 ring ring-grey-200 p-2.5 text-sm rounded ${
									shouldFlashContent ? 'animate-pulse' : ''
								}`}
							>
								<LiveField
									isGenerating={isGeneratingText}
									value={metaCtaText ?? ''}
									onChange={setMetaCtaText}
								/>
							</div>
						</div>
					</div>
				</motion.div>
			</div>
		</ColorProvider>
	)
})
